import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useHasMenu } from 'auth/access';
import { ExpandableTable, SectionTitle, SegmentedControl } from 'modules/shared/components';
import { PROJECT_VIEW_MAP } from 'modules/work-implementation/constants';
import { PROJECT_VIEW_OPTIONS } from '../constants';
import { useAnalystProjectList } from '../hooks/useAnalystProjectList';
import {
  getDisplayProjectStatus,
  getEmptyMessage,
  getResolvedInitialView,
  getVisibleProjects,
} from '../utils';

function getProjectListColumns() {
  const columns = [
    { id: 'code', label: 'Job Code', dataKey: 'code', sortable: true },
    { id: 'date', label: 'Job Date', dataKey: 'date', sortable: true },
    { id: 'name', label: 'Project Name', dataKey: 'name', sortable: true },
    {
      id: 'status',
      label: 'Job Status',
      dataKey: 'status',
      sortable: true,
      renderCell: getDisplayProjectStatus,
    },
  ];
  return columns;
}

export default function AnalystProjectList() {
  const { projectsByStatus, financialYear, loading } = useAnalystProjectList();
  // Both add screens (/work/projects/add and /new) are gated on projects.projects EDIT.
  const canAddProject = useHasMenu('projects.projects', 'EDIT');

  const resolvedInitialView = useMemo(
    () => getResolvedInitialView(projectsByStatus, Object.values(PROJECT_VIEW_MAP)),
    [projectsByStatus],
  );

  const [view, setView] = useState(resolvedInitialView);

  const visibleProjects = useMemo(
    () => getVisibleProjects(projectsByStatus, view),
    [projectsByStatus, view],
  );

  const emptyMessage = useMemo(() => getEmptyMessage(view), [view]);

  const columns = useMemo(() => getProjectListColumns(), []);

  return (
    <div className='column gap-3'>
      <SectionTitle
        title={financialYear ? `MY PROJECTS (F.Y. ${financialYear})` : 'MY PROJECTS'}
        action={
          // The full New Job wizard is no longer offered here; + Add Project is the way in.
          canAddProject && (
            <Link to='/work/projects/add' className='btn cta-outlined-btn'>
              + Add Project
            </Link>
          )
        }
      />

      <SegmentedControl
        options={PROJECT_VIEW_OPTIONS}
        value={view}
        onChange={(v: string) => setView(v)}
        ariaLabel='Project view'
      />

      <ExpandableTable
        columns={columns}
        data={visibleProjects}
        loading={loading}
        emptyMessage={emptyMessage}
        renderExpandedRow={(project) => <ProjectListExpandedRow project={project} />}
        expandColumnWidth='100px'
      />
    </div>
  );
}

function ProjectListExpandedRow({ project }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr 100px',
      }}
      className='row gap-3 row--between'
    >
      <Box sx={{ display: 'flex', gap: 1, gridColumn: 'span 2' }}>
        <span className='font-medium'>Client Name:</span>
        <span>{project.client}</span>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <span className='font-medium'>Job Value:</span>
        <span>{project.value}</span>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <span className='font-medium'>Milestones:</span>
        <span>
          {project.completedMilestones ?? 0}/{project.totalMilestones}
        </span>
      </Box>

      <MuiLink
        component={Link}
        to={`/work/projects/${project.id}`}
        sx={{
          textDecoration: 'underline',
          color: 'grey.500',
          '&:visited': { color: 'grey.500' },
          fontSize: '0.875rem',
          fontWeight: 400,
          whiteSpace: 'nowrap',
          justifySelf: 'end',
        }}
      >
        view details
      </MuiLink>
    </Box>
  );
}
